import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Zap } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import {
  AuthLayout,
  GoogleOAuthButton,
  AuthDivider,
  OtpSignIn,
} from "@/components/auth";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { preloadCriticalRoutes } from "@/lib/preload";

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithGoogle } = useAuth();
  const { toast } = useToast();
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const from = location.state?.from?.pathname || "/app";

  const handleOtpSuccess = () => {
    preloadCriticalRoutes();
    toast({
      title: "Welcome!",
      description: "You have successfully signed in.",
    });
    navigate(from, { replace: true });
  };

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    try {
      await loginWithGoogle();
      // Preload critical routes after Google OAuth completes
      preloadCriticalRoutes();
    } catch (error) {
      toast({
        title: "Google login failed",
        description: "Could not connect with Google. Please try again.",
        variant: "destructive",
      });
      setIsGoogleLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Sign in to your account"
      subtitle="Get started in seconds with Google"
    >
      {/* Google OAuth - Primary/Recommended Option */}
      <div className="space-y-3">
        <div className="flex items-center justify-center gap-2">
          <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20 gap-1">
            <Zap className="h-3 w-3" />
            Recommended
          </Badge>
          <span className="text-xs text-muted-foreground">Instant access</span>
        </div>

        <div className="relative">
          <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 via-accent/20 to-primary/20 rounded-xl blur-sm opacity-75" />
          <div className="relative">
            <GoogleOAuthButton
              onClick={handleGoogleLogin}
              isLoading={isGoogleLoading}
              text="Continue with Google"
            />
          </div>
        </div>

        <p className="text-xs text-center text-muted-foreground">
          No verification email required
        </p>
      </div>

      <AuthDivider text="or sign in with email" />

      <OtpSignIn onSuccess={handleOtpSuccess} />

      <p className="mt-6 text-center text-sm text-muted-foreground">
        Don't have an account?{" "}
        <Link to="/signup" className="text-accent hover:underline font-medium">
          Sign up
        </Link>
      </p>
    </AuthLayout>
  );
}

export default Login;
